"use client";

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { passwordApi } from "@/lib/api/apiwrapper";
import { Button } from "@/components/spendplatform/Button";
import { AlertCircleIcon, ArrowLeftIcon } from "@/components/spendplatform/icons";

export default function Page() {
  const router = useRouter();
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await passwordApi.passwordForgotPasswordPOST({ forgotPasswordDto: { email } });
      setStep("reset");
    } catch (error: any) {
      console.error("Password reset request failed:", error);
      if (error?.status === 400) {
        setError("Please enter a valid email address");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      await passwordApi.passwordResetPasswordPOST({
        resetPasswordDto: {
          email: email,
          token: resetCode,
          password: newPassword,
          confirmPassword: confirmPassword,
        },
      });
      router.push("/auth/sign-in");
    } catch (error: any) {
      console.error("Password reset failed:", error);
      if (error?.status === 400) {
        setError("Invalid reset code or the code has expired");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const header = (
    <>
      <div
        className="sp-eyebrow"
        style={{ marginBottom: "var(--space-3)" }}
      >
        {t("authentication.forgotPassword")}
      </div>
      <h1
        className="sp-h1"
        style={{ marginBottom: "var(--space-3)", lineHeight: "var(--lh-tight)" }}
      >
        {t("authentication.resetPassword.title")}
      </h1>
    </>
  );

  const alert = error && (
    <div
      role="alert"
      className="sp-alert"
      style={{ marginBottom: "var(--space-5)" }}
    >
      <AlertCircleIcon
        size={17}
        className="mt-px shrink-0"
        style={{ color: "var(--sroi-500)" }}
      />
      <span>{error}</span>
    </div>
  );

  if (step === "reset") {
    return (
      <>
        {header}
        <p
          className="sp-body"
          style={{ marginBottom: "var(--space-8)", textWrap: "pretty" }}
        >
          {t("authentication.resetPassword.enterPasswordDescription")}
        </p>

        {alert}

        <form onSubmit={handleResetSubmit}>
          <div
            className="flex flex-col"
            style={{ gap: "var(--space-4)" }}
          >
            <label className="sp-field">
              <span className="sp-label">{t("authentication.resetPassword.resetCodeLabel")}</span>
              <input
                type="text"
                name="resetCode"
                id="resetCode"
                className="sp-input"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                placeholder={t("authentication.resetPassword.resetCodePlaceholder")}
              />
            </label>

            <label className="sp-field">
              <span className="sp-label">{t("authentication.resetPassword.newPasswordLabel")}</span>
              <input
                type="password"
                name="newPassword"
                id="newPassword"
                className="sp-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                placeholder={t("authentication.resetPassword.newPasswordPlaceholder")}
              />
            </label>

            <label className="sp-field">
              <span className="sp-label">{t("authentication.resetPassword.confirmPasswordLabel")}</span>
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                className="sp-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                placeholder={t("authentication.resetPassword.confirmPasswordPlaceholder")}
              />
            </label>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              full
              disabled={isLoading}
            >
              {isLoading ? t("general.resetting") : t("authentication.resetPassword.resetPassword")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="md"
              full
              onClick={() => setStep("email")}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <ArrowLeftIcon size={15} />
                {t("authentication.resetPassword.backToEmail")}
              </span>
            </Button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      {header}
      <p
        className="sp-body"
        style={{ marginBottom: "var(--space-8)", textWrap: "pretty" }}
      >
        {t("authentication.resetPassword.enterEmailDescription")}
      </p>

      {alert}

      <form onSubmit={handleEmailSubmit}>
        <div
          className="flex flex-col"
          style={{ gap: "var(--space-4)" }}
        >
          <label className="sp-field">
            <span className="sp-label">{t("authentication.emailAddress")}</span>
            <input
              type="email"
              name="email"
              id="email"
              className="sp-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              placeholder={t("authentication.signInPage.emailPlaceholder")}
            />
          </label>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            full
            disabled={isLoading}
          >
            {isLoading ? t("general.sending") : t("authentication.resetPassword.sendResetCode")}
          </Button>

          <Link
            href="/auth/sign-in"
            className="sp-link inline-flex w-full items-center justify-center gap-2"
            style={{ marginTop: "var(--space-2)", fontSize: "var(--fs-sm)", fontWeight: "var(--fw-semibold)", color: "var(--color-primary)" }}
          >
            <ArrowLeftIcon size={15} />
            {t("authentication.resetPassword.backToSignIn")}
          </Link>
        </div>
      </form>
    </>
  );
}
